import _ from 'lodash';
import { RefObject, useContext, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { AppContext } from 'src/components/app/contexts/appContext';
import MessageComponent from "./messageComponent";
import { DealRoomContext } from '../../../context';
import { fetchMessages, initiateSmallTalkSession } from 'src/legacy/redux/actions/smallTalk';
import EmptyMessage from 'src/components/emptyMessage';

import styles from "./styles.module.scss";
import { updateUnreadForChatUser } from 'src/legacy/redux/actions/chatUsers';
const NoDiscussionImage = "/images/dealRoom/no_discussion.svg";

export interface Message {
  id: string;
  contents: string[];
  receivedTime: number;
  sentBy: string;
}

export interface UserDetails {
  name: string;
  profilePicture: string;
  online: boolean;
}

const scrollToBottom = (targetRef: RefObject<HTMLDivElement | null>) => {
  if (targetRef?.current) {
    const scrollHeight = targetRef.current.scrollHeight;
    if (scrollHeight !== undefined) {
      targetRef.current.scrollTop = scrollHeight;
    }
  }
}

const ChatSection: React.FC = () => {
  const dispatch : any = useDispatch();
  const { smallTalk } = useContext(AppContext);
  const { dealRoom } = useContext(DealRoomContext);
  const dealRoomId = _.get(dealRoom, 'id', '');

  const chatBottomRef = useRef<HTMLDivElement | null>(null);

  const { conversationId } = useParams();
  const activeChatUserId = conversationId ?? '';

  const me = useSelector((state: any) => {
    return _.get(state, 'users.me', null);
  });

  const messages = useSelector((state: any) => {
    const messagesFromStore = _.get(state, 'legacyRootReducer.smallTalk.session.messages.items', []);
    const alteredMessages = messagesFromStore?.map((message: any) => {
      return {
        id: _.get(message, 'id', ''),
        contents: [_.get(message, 'text', '')],
        receivedTime: _.get(message, 'timestamp', Date.now()),
        sentBy: _.get(message, 'sender.id', ''),
        sender: _.get(message, "sender", {}),
      }
    })

    return alteredMessages;
  });

  const activeChatUserData = useSelector((state: any) => {
    return _.get(state, `dealRooms.dealRoom.${dealRoomId}.engage.activeChatUser`, false);
  });

  const activeChatUser = _.get(activeChatUserData, 'item', {});
  const activeChatUserLoading = _.get(activeChatUserData, 'loading', false);
  const channelName = _.get(activeChatUser, 'uuid', []);

  const smallTalkSessionData = useSelector((state: any) => {
    return _.get(state, 'legacyRootReducer.smallTalk.session', null);
  });

  const smallTalkSessionId = _.get(smallTalkSessionData, 'contact.id', '');
  const isMessagesLoading = _.get(smallTalkSessionData, 'messages.loading', false);

  useEffect(() => {
    if (activeChatUserId && activeChatUser) {
      dispatch(initiateSmallTalkSession(activeChatUser));
    }

  }, [activeChatUserLoading, activeChatUserId]);

  useEffect(() => {
    if(channelName){
      dispatch(updateUnreadForChatUser(channelName[0]))
    }
  },[channelName])

  useEffect(() => {
    if (!activeChatUserLoading && activeChatUserId && smallTalkSessionId && (
      activeChatUserId === smallTalkSessionId
    )) {
      getMessages();
    }
  }, [smallTalkSessionId, activeChatUserId, activeChatUserLoading]);

  useEffect(() => {
    if (chatBottomRef) {
      scrollToBottom(chatBottomRef);
    }
  }, [messages]);

  const isCollaborator = (message: Message) => {
    return _.get(message, "sender.type") === "user";
  }

  const isSender = (message: Message) => {
    return `${_.get(message, 'sentBy', '')}` === `${_.get(me, 'id', '')}`;
  }

  const getMessageUser = (message: Message) => {
    const user = isSender(message) ? me : activeChatUser;
    const icon = _.get(user, 'contactDTO.icon.thumbnail', _.get(user,"icon.thumbnail",""));

    return {
      name: _.get(message, "sender.name", ""),
      profilePicture: isCollaborator(message) && !isSender(message) ? "" : icon,
      online: isSender(message) ? true : _.get(user, 'online', false),
    };
  };
  
  const getMessages = () => {
    dispatch(fetchMessages(smallTalk.client, channelName, 100) as any);
  }

  return (
    <div 
      className={styles.chatSectionContainer}
      ref={chatBottomRef}
    >
      {
        !activeChatUserLoading && !isMessagesLoading && _.size(messages) > 0 && _.map(messages, (message: Message) => {
          return (
            <MessageComponent
              message={message}
              user={getMessageUser(message)}
              isSender={isCollaborator(message)}
              key={message?.id}
              showOnlineStatus={!(isCollaborator(message) && !isSender(message))}
            />
          )
        })
      }
      {
        !activeChatUserLoading && !isMessagesLoading && _.size(messages) === 0 && (
          <div className={styles.emptyMesssageContainer}>
            <EmptyMessage
              image={NoDiscussionImage}
              heading="No conversations yet. Start engaging with your buyers."
              message=""
            />
          </div>
        )
      }
      {
        activeChatUserLoading || isMessagesLoading && (
          <div className={styles.emptyMesssageContainer}>
            <p>Loading...</p>
          </div>
        )
      }
    </div>
  );
}

export default ChatSection;